import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FormContainer from "../../component/shared/FormContainer";
import CustomInput from "../../component/shared/CustomInput";
import Button from "../../component/shared/Button";
import { Api } from "../../network/api";

const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await Api.forgotPassword(formData);

      if (response.data?.success || response.status === 200) {
        setSuccess(true);
      } else {
        setErrors({ general: "Failed to send reset email. Please try again." });
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send reset email. Please try again.";
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (success) {
    return (
      <FormContainer
        title="Reset Email Sent"
        subtitle="Check your admin email inbox"
      >
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-left animate-fadeInUp">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Password reset email sent successfully!
            </div>
          </div>

          <div className="text-center space-y-4 animate-fadeInUp animation-delay-100">
            <p className="text-sm text-gray-600">
              We've sent a password reset link to{" "}
              <strong>{formData.email}</strong>
            </p>
            <p className="text-xs text-gray-500">
              Please check your admin email and click the link to reset your
              password. The link will expire in 24 hours for security.
            </p>
          </div>

          <div className="animate-fadeInUp animation-delay-200">
            <Button
              type="button"
              onClick={handleBackToLogin}
              className="w-full hover-lift"
              size="large"
            >
              Back to Admin Login
            </Button>
          </div>

          <div className="text-center animate-fadeInUp animation-delay-300">
            <p className="text-sm text-gray-600">
              Didn't receive the email?{" "}
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="font-medium text-red-600 hover:text-red-500 transition-colors duration-200 hover:underline"
              >
                Try again
              </button>
            </p>
          </div>
        </div>
      </FormContainer>
    );
  }

  return (
    <FormContainer
      title="Reset Admin Password"
      subtitle="Enter your admin email to receive a reset link"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-left animate-shake">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.general}
            </div>
          </div>
        )}

        <div className="animate-fadeInUp animation-delay-100">
          <CustomInput
            type="email"
            name="email"
            label="Email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
            autoComplete="email"
          />
        </div>

        <div className="animate-fadeInUp animation-delay-200">
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full hover-lift"
            size="large"
          >
            {loading ? "Sending Reset Link..." : "Send Reset Link"}
          </Button>
        </div>

        <div className="text-center animate-fadeInUp animation-delay-300">
          <p className="text-sm text-gray-600">
            Remember your password?{" "}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="font-medium text-red-600 hover:text-red-500 transition-colors duration-200 hover:underline"
            >
              Back to admin login
            </button>
          </p>
        </div>
      </form>
    </FormContainer>
  );
};

export default ForgotPasswordForm;
